import { FolderTreeSelect } from '../../CommonComponents/FolderTreeSelect/FolderTreeSelect';
import { FOLDER_SELECT_MENU_HEIGHT } from '@opengov/stories-shared/constants';
import { IPublication } from '@opengov/stories-shared/types';
import { RouteComponentProps } from '@reach/router';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState } from 'react';

import { ISessionUser, ISettings, IStoriesFolder, IStoryListMessages } from '../../../../types';

import { useCreateStory } from '../hooks';

import { styles } from './CreateStoryModal.styles';

export interface ICreateStoryModalProps extends RouteComponentProps {
  isAdmin: boolean;
  messages: IStoryListMessages;
  canOrganizeStories: boolean;
  currentFolderId?: string;
  folderStructure: IStoriesFolder[];
  user: ISessionUser;
  entityName: string;
  entityId: number;
  entitySubdomain: string;
  settings?: ISettings;
  publication?: IPublication;
  onClose(): void;
  updateThumbnail?(publicationId: string, controller?: AbortController): void;
}

export const CreateStoryModal: React.FC<ICreateStoryModalProps> = (props) => {
  const {
    isAdmin,
    canOrganizeStories,
    currentFolderId,
    folderStructure,
    messages,
    entityName,
    entityId,
    entitySubdomain,
    settings,
    user,
    publication,
    onClose
  } = props;
  const createStoryMutation = useCreateStory();
  const [storyTitle, setStoryTitle] = useState('');
  const [storyDescription, setStoryDescription] = useState('');
  const [folderId, setFolderId] = useState(
    currentFolderId ? currentFolderId : folderStructure && folderStructure[0] && folderStructure[0].id
  );

  const handleStoryCreate = () => {
    const { updateThumbnail } = props;
    createStoryMutation.mutate({
      isAdmin,
      isTemplate: false,
      user,
      entityName,
      title: storyTitle,
      description: storyDescription,
      entityId,
      entitySubdomain,
      settings,
      folderId,
      publication
    });
    publication?.id && updateThumbnail && updateThumbnail(publication.id);
    onClose();
  };

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: styles.dialogPaper }} aria-labelledby="create-story-dialog-title">
      <DialogTitle id="create-story-dialog-title" sx={styles.dialogTitle}>
        {messages.createStory}
        <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Box data-test="create-story-form">
          <Box sx={styles.formItem}>
            <Box sx={styles.labelRow}>
              <Typography id="create-story-title-label" variant="body2" sx={styles.formLabel}>
                {messages.title}
              </Typography>
              <Typography variant="caption" sx={styles.characterCounter}>
                {storyTitle.length}/60
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              sx={styles.textField}
              placeholder={messages.storyName}
              value={storyTitle}
              onChange={(e) => setStoryTitle(e.target.value)}
              inputProps={{ 'data-test': 'story-title-input', maxLength: 60, className: 'no-format', 'aria-labelledby': 'create-story-title-label' }}
            />
          </Box>

          <Box sx={styles.formItem}>
            <Box sx={styles.labelRow}>
              <Typography id="create-story-description-label" variant="body2" sx={styles.formLabel}>
                {messages.description}
              </Typography>
              <Typography variant="caption" sx={styles.characterCounter}>
                {storyDescription.length}/150
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              sx={styles.textField}
              placeholder={messages.storyDescription}
              value={storyDescription}
              onChange={(e) => setStoryDescription(e.target.value)}
              inputProps={{ 'data-test': 'story-description-input', maxLength: 150, className: 'no-format', 'aria-labelledby': 'create-story-description-label' }}
            />
          </Box>

          {canOrganizeStories && (
            <Box sx={styles.listItem}>
              <Typography variant="body2" sx={styles.formLabel}>
                {messages.location}
              </Typography>
              <FolderTreeSelect
                activeNodeId={folderId}
                data={folderStructure}
                dataTest="folder-accordion-select"
                disabled={!folderStructure}
                maxMenuHeight={FOLDER_SELECT_MENU_HEIGHT}
                onNodeSelect={(node) => setFolderId(node.id)}
                showFilter={true}
                filterPlaceholder={messages.filterFolders}
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <Button variant="outlined" color="secondary" size="small" onClick={onClose} data-test="cancel-create-story-button">
          {messages.cancel}
        </Button>
        <Button
          variant="contained"
          size="small"
          color="primary"
          disabled={
            storyTitle.length < 1 ||
            storyTitle.length > 60 ||
            storyDescription.length > 150 ||
            (publication?.id.length && folderStructure.length < 1)
          }
          onClick={handleStoryCreate}
          data-test="create-story-button"
        >
          {messages.create}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
